// Public page describing the diibs text messaging program and every place a
// person opts in, with screenshots. SMS carriers review it for the 10DLC
// campaign (PDE-414): keep the disclosures in step with SmsConsentNote and
// the app's AppStrings.smsConsent, and replace a screenshot when its screen
// changes. Screenshots live in public/sms/.

type OptIn = {
  title: string;
  where: string;
  image: string;
  alt: string;
};

const SELF_DISCLOSURE =
  'By entering your phone number, you agree to receive verification codes and reservation texts from diibs. Msg frequency varies. Msg & data rates may apply. Reply STOP to opt out, HELP for help. See our Terms and Privacy Policy.';

const HOST_DISCLOSURE =
  'diibs will text this number a verification code, then attendance confirmations for reservations at your restaurant. Only add a host who has agreed to receive them. Msg frequency varies. Msg & data rates may apply. Reply STOP to opt out, HELP for help. See our Terms and Privacy Policy.';

const DINER_OPT_INS: OptIn[] = [
  {
    title: 'Sign up',
    where: 'diibs app (iOS and Android), Sign Up screen',
    image: '/sms/1-signup.png',
    alt: 'diibs app Sign Up screen with the phone number field and the SMS consent note below it',
  },
  {
    title: 'Account Information',
    where: 'diibs app, Profile > Account Information',
    image: '/sms/2-account-info.png',
    alt: 'diibs app Account Information screen with the phone number field and the SMS consent note below it',
  },
];

const RESTAURANT_OPT_INS: OptIn[] = [
  {
    title: 'Restaurant registration',
    where:
      'diibs vendor portal, Register Restaurant form: the consent text is under the phone field, followed by a required "I agree to receive verification codes and reservation texts from diibs" checkbox',
    image: '/sms/3-restaurant-signup.png',
    alt: 'Vendor portal restaurant registration form with the SMS consent note and required SMS consent checkbox below the phone field',
  },
  {
    title: 'Restaurant profile',
    where: 'diibs vendor portal, Profile > verify mobile number',
    image: '/sms/4-restaurant-profile.png',
    alt: 'Vendor portal mobile number verification dialog with the SMS consent note below the phone field',
  },
];

const HOST_OPT_INS: OptIn[] = [
  {
    title: 'Adding a host',
    where: 'diibs vendor portal, Manage Host > Add host',
    image: '/sms/5-add-host.png',
    alt: 'Vendor portal Add host dialog with the host SMS consent note below the phone field',
  },
];

const OptInList = ({items, disclosure}: {items: OptIn[]; disclosure: string}) => (
  <>
    <p className='mb-4 fs-5'>The screen shows this text directly under the phone number field:</p>
    <blockquote className='mb-6 fs-5 border-start border-4 ps-4 fst-italic'>{disclosure}</blockquote>
    <div className='row g-6 mb-8'>
      {items.map((item) => (
        <figure key={item.image} className='col-md-6 mb-0'>
          <img
            src={item.image}
            alt={item.alt}
            className='img-fluid rounded border'
            style={{maxHeight: 640}}
            loading='lazy'
          />
          <figcaption className='mt-2 fs-6'>
            <strong>{item.title}:</strong> {item.where}
          </figcaption>
        </figure>
      ))}
    </div>
  </>
);

const SmsTerms = () => (
  <div className='container py-4 mt-10'>
    <h1 className='fw-bold fs-1 mb-6 text-center'>diibs Text Messages</h1>

    <p className='mb-6 fs-4'>
      diibs (Diibs, Inc.) runs a restaurant reservation marketplace at gotdiibs.com and in the diibs
      iOS and Android apps, where diners bid on or buy reservations at partner restaurants. This
      page describes the text messages we send, how people agree to receive them, and how to stop
      them.
    </p>

    <h2 className='fw-bold fs-2 mb-4 link-primary'>Messages we send</h2>
    <ul className='mb-6 fs-4'>
      <li>One-time verification codes when a diner, restaurant or host adds or verifies a phone number.</li>
      <li>
        Reservation notices to diners about their booking, such as a check-in reminder or a
        cancellation warning.
      </li>
      <li>
        Attendance confirmations to a partner restaurant&apos;s host at dining time, who replies
        with 1, 2 or 0 and the booking code to say whether the guest arrived, with one follow-up if
        there is no reply.
      </li>
    </ul>
    <p className='mb-6 fs-4'>
      All messages are transactional. We do not send marketing texts, and we do not share phone
      numbers or opt-in data with third parties or affiliates for marketing.
    </p>

    <h2 className='fw-bold fs-2 mb-4 link-primary'>How people opt in</h2>
    <p className='mb-6 fs-4'>
      Each person agrees to texts when they enter their phone number in one of the screens below.
      Diner and host numbers, and restaurant numbers changed from the restaurant profile, are
      confirmed with a one-time code texted to the number. Restaurants registering also tick a
      required consent box.
    </p>

    <h3 className='fw-bold fs-3 mb-4'>Diners (diibs app)</h3>
    <OptInList items={DINER_OPT_INS} disclosure={SELF_DISCLOSURE} />

    <h3 className='fw-bold fs-3 mb-4'>Restaurants (diibs vendor portal)</h3>
    <OptInList items={RESTAURANT_OPT_INS} disclosure={SELF_DISCLOSURE} />

    <h3 className='fw-bold fs-3 mb-4'>Restaurant hosts (added by the restaurant)</h3>
    <OptInList items={HOST_OPT_INS} disclosure={HOST_DISCLOSURE} />

    <h2 className='fw-bold fs-2 mb-4 link-primary'>Frequency, cost and help</h2>
    <ul className='mb-6 fs-4'>
      <li>Message frequency varies with your reservations.</li>
      <li>Message and data rates may apply.</li>
      <li>
        Reply <strong>STOP</strong> to any message to opt out. Reply <strong>START</strong> to opt
        back in.
      </li>
      <li>
        Reply <strong>HELP</strong> for help, or email{' '}
        <a href='mailto:info@gotdiibs.com'>info@gotdiibs.com</a>.
      </li>
      <li>Carriers are not liable for delayed or undelivered messages.</li>
    </ul>
    <p className='mb-6 fs-4'>
      See our <a href='/terms-and-conditions'>Terms and Conditions</a> and{' '}
      <a href='/privacy-policy'>Privacy Policy</a>.
    </p>
  </div>
);

export default SmsTerms;
